<script setup>
/* global axios */
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { capabilityState, clearCapabilities, commitCapabilities, accountAttentionEnabled } from './capabilities';

const user = useMapGetter('getCurrentUser');
const route = useRoute();
const failure = ref('');
let timer; let controller; let generation = 0; let pending = false; let disposed = false;

async function refresh() {
  if (disposed || pending || !user.value?.id) return;
  const epoch = generation; const actor = user.value.id;
  pending = true; controller = new AbortController();
  try {
    const { data } = await axios.get('/api/v1/attention_capabilities', { signal: controller.signal, timeout: 2500 });
    if (disposed || epoch !== generation || user.value?.id !== actor) return;
    commitCapabilities(data, actor); failure.value = '';
  } catch (error) {
    if (disposed || epoch !== generation) return;
    if ([401, 403].includes(error.response?.status)) clearCapabilities();
    failure.value = 'Alert setup cannot be reconciled. Check your connection or sign in again.';
  } finally { if (epoch === generation) pending = false; }
}
function reset() {
  generation += 1; controller?.abort(); pending = false; clearCapabilities(); refresh();
}
watch(() => user.value?.id, reset);
onMounted(() => {
  refresh();
  // A successful attention snapshot also commits capabilities. This produces
  // one lightweight poll in either mode rather than two active pollers.
  timer = setInterval(() => { if (!accountAttentionEnabled(route.params.accountId) || Date.now() - capabilityState.updatedAt >= 6000) refresh(); }, 4000);
  for (const event of ['attention:capability-refresh', 'focus', 'pageshow', 'online']) window.addEventListener(event, refresh);
});
onUnmounted(() => {
  disposed = true; generation += 1; controller?.abort(); clearInterval(timer); clearCapabilities();
  for (const event of ['attention:capability-refresh', 'focus', 'pageshow', 'online']) window.removeEventListener(event, refresh);
});
</script>

<template>
  <p v-if="failure" class="px-3 py-2 text-sm text-n-slate-12 bg-n-background border-b border-n-weak" role="status">{{ failure }}</p>
</template>
